import { motion, AnimatePresence } from 'framer-motion';
import type { ActivityType } from '@/types/running';

interface ActivityTypePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: ActivityType) => void;
}

const activityTypes: { value: ActivityType; label: string; emoji: string }[] = [
  { value: 'run', label: 'Run', emoji: '🏃' },
  { value: 'walk', label: 'Walk', emoji: '🚶' },
  { value: 'cycle', label: 'Cycle', emoji: '🚴' },
  { value: 'hike', label: 'Hike', emoji: '🥾' },
  { value: 'trail_run', label: 'Trail Run', emoji: '🌲' },
  { value: 'yoga', label: 'Yoga', emoji: '🧘' },
  { value: 'swim', label: 'Swim', emoji: '🏊' },
  { value: 'strength', label: 'Strength', emoji: '🏋️' },
  { value: 'treadmill', label: 'Treadmill', emoji: '🏃‍♂️' },
  { value: 'other', label: 'Other', emoji: '⚡' },
];

export function ActivityTypePicker({ isOpen, onClose, onSelect }: ActivityTypePickerProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
          />

          {/* Bottom sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-[61] bg-[#0d1117] border-t border-white/10 rounded-t-3xl max-h-[70vh] overflow-y-auto"
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-white/20 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4">
              <h2 className="text-xl font-bold text-white">Choose Activity</h2>
              <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Activity grid */}
            <div className="grid grid-cols-3 gap-3 px-6 pb-8">
              {activityTypes.map((type) => (
                <motion.button
                  key={type.value}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onSelect(type.value)}
                  className="flex flex-col items-center gap-2 py-5 px-3 bg-white/5 hover:bg-emerald-500/20 border border-white/10 hover:border-emerald-500/50 rounded-2xl transition-all"
                >
                  <span className="text-3xl">{type.emoji}</span>
                  <span className="text-sm font-medium text-gray-300">{type.label}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
